import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { productGroupsAPI, categoriesAPI } from '../services/api'
import { Plus, Search, Filter, Edit, Trash2, Eye, Folder, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { usePermissions } from '../hooks/usePermissions'
import { createNavigationHelpers } from '../utils/navigationHelpers'
import ConfirmationModal from '../components/ConfirmationModal'

export default function ProductGroups() {
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const [activeFilter, setActiveFilter] = useState('true')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [productGroupToDelete, setProductGroupToDelete] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { productGroups: productGroupPerms } = usePermissions()
  const { goToCreate, goToEdit } = createNavigationHelpers('product-groups', navigate)

  // Fetch product groups
  const { data: productGroupsData, isLoading, error } = useQuery(
    ['product-groups', currentPage, search, categoryFilter, activeFilter],
    () => productGroupsAPI.getAll({
      page: currentPage,
      limit: 12,
      search,
      category_uuid: categoryFilter,

      is_active: activeFilter
    }),
    {
      retry: 1,
      onError: (error) => {
        console.error('Product groups fetch error:', error);
        toast.error('Failed to load product groups');
      }
    }
  )

  // Fetch categories for filter
  const { data: categoriesData } = useQuery(
    'categories',
    () => categoriesAPI.getAll(),
    {
      retry: 1,
      onError: (error) => {
        console.error('Categories fetch error:', error);
      }
    }
  )

  // No longer need subcategories since we're linking directly to categories

  // Delete product group mutation
  const deleteMutation = useMutation(
    (uuid) => productGroupsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('product-groups')
        toast.success('Product group deleted successfully')
        setDeleteModalOpen(false)
        setProductGroupToDelete(null)
      },
      onError: (error) => {
        console.error('Delete error:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete product group'
        toast.error(errorMessage)
      }
    }
  )

  // Toggle product group status mutation
  const toggleMutation = useMutation(
    (uuid) => productGroupsAPI.toggleStatus(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('product-groups')
        toast.success('Product group status updated successfully')
      },
      onError: () => {
        toast.error('Failed to update product group status')
      }
    }
  )

  const handleDelete = (productGroup) => {
    setProductGroupToDelete(productGroup)
    setDeleteModalOpen(true)
  }

  const confirmDelete = () => {
    if (productGroupToDelete) {
      console.log('Attempting to delete product group:', productGroupToDelete)
      console.log('Product Group UUID:', productGroupToDelete.uuid)
      deleteMutation.mutate(productGroupToDelete.uuid)
    } else {
      console.error('No product group selected for deletion')
      toast.error('No product group selected')
    }
  }

  const handleEdit = (productGroup) => {
    goToEdit(productGroup.uuid)
  }

  const handleCreate = () => {
    goToCreate()
  }

  const handleView = (productGroup) => {
    // Show detailed view modal or navigate to view page
    // For now, we'll show an alert with details (you can improve this later)
    const details = `
Product Group: ${productGroup.name}
Category: ${productGroup.category_name || 'N/A'}
Status: ${productGroup.is_active ? 'Active' : 'Inactive'}
Created: ${new Date(productGroup.created_at).toLocaleDateString()}
Updated: ${new Date(productGroup.updated_at).toLocaleDateString()}
    `.trim()
    
    alert(details)
  }

  const handleToggleStatus = (productGroup) => {
    toggleMutation.mutate(productGroup.uuid)
  }

  // Ensure product groups is always an array
  const productGroups = Array.isArray(productGroupsData?.data?.data) ? productGroupsData.data.data : []
  const pagination = productGroupsData?.data?.pagination || { page: 1, limit: 12, total: 0, pages: 1 }
  
  // Ensure categories is always an array
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : []
  
  // Handle category change
  const handleCategoryChange = (categoryId) => {
    console.log('Category filter changed to:', categoryId);
    setCategoryFilter(categoryId);
  }

  const clearFilters = () => {
    setSearch('')
    setCategoryFilter('')
    setActiveFilter('')
  }

  if (!productGroupPerms.canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to view product groups.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Product Groups</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage product groups and organize your inventory
            </p>
          </div>
          {/* Add Product Group Button - Only show if user can create product groups */}
          {productGroupPerms.canCreate && (
            <button
              onClick={handleCreate}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Add Product Group</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white shadow-glow">
            <Filter className="h-5 w-5" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
          {/* Search */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Search</label>
            <div className="search-input-container">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search product groups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="input-modern w-full"
            >
              <option value="">All Categories</option>
              {categories?.map((category) => {
                const categoryId = category.uuid;
                return (
                  <option key={categoryId} value={categoryId}>
                    {category.name}
                  </option>
                );
              })}
            </select>
          </div>



          {/* Active Status */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="input-modern w-full"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="btn-secondary w-full text-sm px-4 py-2 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading product groups...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Failed to load product groups</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Please try again later</p>
          <button
            onClick={() => queryClient.invalidateQueries('product-groups')}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      )}

      {/* Product Groups Grid */}
      {!isLoading && !error && (
        <>
          {productGroups.length === 0 ? (
            <div className="card-modern text-center py-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="text-gray-400 text-6xl mb-4">📁</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No product groups found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {search || categoryFilter || activeFilter ? 
                  'Try adjusting your filters to find product groups.' : 
                  'Get started by creating your first product group.'
                }
              </p>
              {productGroupPerms.canCreate && !search && !categoryFilter && (
                <button
                  onClick={handleCreate}
                  className="btn-primary px-6 py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Product Group
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              {productGroups.map((productGroup) => (
                <div key={productGroup.uuid} className="card-modern group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {/* Header with folder icon */}
                  <div className="w-full h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white mb-4">
                    <Folder className="h-8 w-8" />
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {productGroup.name}
                      </h3>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-medium">Category:</span> {productGroup.category_name || 'N/A'}
                      </p>

                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleToggleStatus(productGroup)}
                        disabled={!productGroupPerms.canUpdate || toggleMutation.isLoading}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-all duration-200 ${
                          productGroup.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-700'
                            : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-700'
                        } ${
                          productGroupPerms.canUpdate && !toggleMutation.isLoading
                            ? 'cursor-pointer hover:scale-105'
                            : 'cursor-not-allowed opacity-75'
                        }`}
                        title={productGroupPerms.canUpdate ? 'Click to toggle status' : 'No permission to change status'}
                      >
                        {productGroup.is_active ? 'Active' : 'Inactive'}
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleView(productGroup)}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        {productGroupPerms.canUpdate && (
                          <button
                            onClick={() => handleEdit(productGroup)}
                            className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                            title="Edit product group"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        
                        {productGroupPerms.canDelete && (
                          <button
                            onClick={() => handleDelete(productGroup)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                            title="Delete product group"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center space-x-4 mt-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <button
                onClick={() => setCurrentPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
              
              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg ${
                        pagination.page === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'btn-secondary'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="btn-secondary px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Results info */}
          {productGroups.length > 0 && (
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} product groups
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Product Group"
        message={
          productGroupToDelete
            ? `Are you sure you want to delete "${productGroupToDelete.name}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        confirmButtonClass="btn-danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  )
}